import { FC, KeyboardEvent, useState } from "react";

import { useFormikContext } from "formik";
import { useTranslation } from "next-i18next";

import { FullWidthVStack } from "@components/common";
import Expand from "@components/Expand";
import Icon from "@components/Icon";
import Message from "@components/Message";

import useCreateAddress from "../hooks/useCreateAddress";
import useHasNewRecipientOrderErrors from "../hooks/useHasNewRecipientOrderErrors";
import AddressForm from "./AddressForm";
import ExpandContent from "./ExpandContent";
import ExpandHeader from "./ExpandHeader";
import LockerForm from "./LockerForm";
import SubmitComponent from "./SubmitComponent";

type TNewRecipientProps = {
  isExpanded: boolean;
  isLockerForm?: boolean;
  country: string;
  onExpandChange: (isExpanded: boolean) => void;
};

const NewRecipient: FC<TNewRecipientProps> = ({ isExpanded, isLockerForm, country, onExpandChange }) => {
  const { t } = useTranslation();

  const { submitCount } = useFormikContext();

  const [isSaveSuccess, setIsSaveSuccess] = useState(false);

  const handleExpandChange = (isOpen: boolean) => {
    onExpandChange(isOpen);
  };

  const { isLoading, handleFormSubmit } = useCreateAddress(!!isLockerForm);

  const hasOrderError = useHasNewRecipientOrderErrors(isExpanded);

  // As this is form inside another form, we have to manually handle validation and data submit
  const handleSubmitAddress = async () => {
    const isSuccess = await handleFormSubmit();
    setIsSaveSuccess(isSuccess);
  };

  // This is to prevent main form submit
  const handleFormKeyDown = (evt: KeyboardEvent<HTMLDivElement>) => {
    if (evt.key === "Enter") {
      evt.preventDefault();
      handleSubmitAddress();
    }
  };

  return (
    <Expand
      renderHeader={(isOpen) => <ExpandHeader isOpen={isOpen} />}
      isOpen={isExpanded}
      onChange={handleExpandChange}
      id="order-new_recipient-form"
    >
      <ExpandContent>
        <FullWidthVStack spacing="1.25rem" onKeyDown={handleFormKeyDown}>
          {/* Submit count is used to show this error only after submit */}
          {hasOrderError && submitCount > 0 && (
            <Message
              variant="error"
              icon={<Icon icon="warning" size="1.25rem" />}
              sx={{ fontWeight: 300, height: "3.125rem" }}
            >
              {t("order-add_profile_to_address_book_before_proceeding_with_order")}
            </Message>
          )}
          {isLockerForm ? <LockerForm country={country} /> : <AddressForm country={country} />}
          <SubmitComponent isSaveSuccess={isSaveSuccess} isLoading={isLoading} onSubmit={handleSubmitAddress} />
        </FullWidthVStack>
      </ExpandContent>
    </Expand>
  );
};
export default NewRecipient;
